import Hearing from "@mui/icons-material/Hearing";
import Inbox from "@mui/icons-material/Inbox";
import Language from "@mui/icons-material/Language";
import PermMedia from "@mui/icons-material/PermMedia";
import PieChartOutlineRounded from "@mui/icons-material/PieChartOutlineRounded";
import Send from "@mui/icons-material/Send";
import Timeline from "@mui/icons-material/Timeline";
import type { FC } from "react";
import { usePermissions } from "../../../../../server/auth/use-permissions";
import { useFlag } from "../../../../../server/optimizely";
import { useAccountLimitsContext } from "../../../../context/account-limit-context";
import { useAnalyticsPerms } from "../../../../employees/hooks/use-analytics";
import { useAccount } from "../../../../generic/hooks/use-account";
import { useInboxCount } from "../hooks/use-inbox-count";
import { useIsActive } from "../hooks/use-is-active";
import { Item } from "../item";
import { Programs } from "../programs";
import { Section } from "../section";
import { NavbarBadge } from "./navbar-badge";

type NavOptionsProps = {
  onNavigate?: () => void;
};

export const NavOptions: FC<NavOptionsProps> = ({ onNavigate }) => {
  const { data: account } = useAccount();
  const [inbox_menu_option] = useFlag("sms_menu_option");

  const isActive = useIsActive();
  const perms = usePermissions();

  const analyticsPerms = useAnalyticsPerms();
  const showRecipientActivityPage = perms.super_admin;

  const { listen_entitlement } = useAccountLimitsContext();

  const unread_conversation_count = useInboxCount();

  return (
    <Section>
      <>
        <Item
          data-analytics-id="sidebar-content-library"
          title="Content Library"
          icon={<PermMedia />}
          onClick={{ type: "href", value: "/content-library" }}
          onNavigate={onNavigate}
          active={isActive("/content-library")}
        />
        <Item
          data-analytics-id="sidebar-discover"
          title="Discover"
          icon={<Language />}
          onClick={{ type: "href", value: "/discover" }}
          onNavigate={onNavigate}
          active={isActive("/discover", true)}
        />
        <Item
          data-analytics-id="sidebar-listen"
          title="Listen"
          icon={<Hearing />}
          onClick={{ type: "href", value: "/listen" }}
          onNavigate={onNavigate}
          active={isActive("/listen", true)}
          label={listen_entitlement === "Pro" ? null : <NavbarBadge badgeContent="New" />}
        />
        <Programs />
        <Item
          data-analytics-id="sidebar-send-communications"
          title="Sent Communications"
          icon={<Send />}
          onClick={{ type: "href", value: "/moments/history" }}
          onNavigate={onNavigate}
          active={isActive("/moments/history")}
        />
        {analyticsPerms.length > 0 && (
          <Item
            data-analytics-id="sidebar-analytics"
            title="Analytics"
            icon={<PieChartOutlineRounded />}
            onClick={{ type: "href", value: "/analytics" }}
            onNavigate={onNavigate}
            active={isActive("/analytics")}
            label={<NavbarBadge badgeContent="Beta" />}
          />
        )}

        {showRecipientActivityPage === true && (
          <Item
            data-analytics-id="sidebar-recipient-activity"
            title="Recipient Activity"
            icon={<Timeline />}
            onClick={{ type: "href", value: "/recipient-activity" }}
            onNavigate={onNavigate}
            active={isActive("/recipient-activity", true)}
          />
        )}

        {inbox_menu_option && (
          <Item
            data-analytics-id="sidebar-inbox"
            title="Inbox"
            icon={<Inbox />}
            onClick={{ type: "href", value: "/inbox" }}
            onNavigate={onNavigate}
            active={isActive("/inbox", true)}
            label={
              account && !account.enable_twilio ? (
                <NavbarBadge color="primary" badgeContent="New" />
              ) : unread_conversation_count ? (
                <NavbarBadge color="primary" badgeContent={unread_conversation_count} />
              ) : null
            }
          />
        )}
      </>
    </Section>
  );
};
